import { Tooltip } from '@mantine/core'
import {
  first,
  get,
  isArray,
  isFunction,
  isPlainObject,
  map,
  noop,
} from 'lodash'
import React from 'react'
import { components } from 'react-select'
import { SemanticCOLORS } from 'semantic-ui-react'
import { CSSObject } from 'styled-components'

import Icon from '../Icon'
import {
  ICustomStyle,
  TGetCustomElement,
  TGetCustomStyle,
  TOption,
  TPrefixedValueContainer,
  TSlimIndicatorContainer,
} from './types'

export const PrefixedValueContainer =
  (prefix: string | JSX.Element) =>
  // eslint-disable-next-line react/display-name
  (props: TPrefixedValueContainer): React.ReactElement =>
    (
      <components.ValueContainer {...props}>
        <span>{prefix}</span>
        <span>{props.children}</span>
      </components.ValueContainer>
    )

export const SlimIndicatorsContainer = (
  props: TSlimIndicatorContainer,
): React.ReactElement => {
  return (
    <div style={{ border: '2px solid orange' }} hidden>
      <components.IndicatorsContainer {...props} />
    </div>
  )
}

export const IndicatorsContainer = (): React.ReactElement | null => null
export const IndicatorSeparator = (): React.ReactElement | null => null

export const flattenGroups = (options: any[]): TOption[] => {
  return options.map((option) =>
    isArray(option.options) ? flattenGroups(option.options) : option,
  )
}

export const getOptions = (
  options: any[] | { [key: string]: string },
): TOption[] => {
  const opts = options
  if (isPlainObject(opts)) {
    return map(opts, (value, key) => ({
      label: value as string,
      value: key,
    }))
  }
  if (isArray(opts)) {
    const firstOption = first(opts)
    if (firstOption && !isPlainObject(firstOption)) {
      return opts.map((o) => ({ label: o, value: o }))
    }
    return opts
  }
  return []
}

export const getCustomStyle = ({
  styles = {},
  enableiOSResizeFix = false,
  small = false,
  noStyle = false,
  artificalDarkMode = false,
  color,
  isInvalid = false,
}: TGetCustomStyle): ICustomStyle => {
  const menuStyle = get(styles, 'menu') || {}
  const menuPortalStyle = get(styles, 'menuPortal') || {}

  let customStyles: ICustomStyle = {
    ...(styles || {}),
    menu: (provided: CSSObject) => ({
      ...provided,
      ...(isFunction(menuStyle) ? menuStyle(provided) : menuStyle),
      zIndex: 9999,
    }),
    menuPortal: (provided: CSSObject) => ({
      ...provided,
      ...(isFunction(menuPortalStyle)
        ? menuPortalStyle(provided)
        : menuPortalStyle),
      zIndex: 9999,
    }),
  }

  const deviceIsiOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && (!window as any).MSStream
  if (deviceIsiOS && enableiOSResizeFix) {
    customStyles = {
      ...customStyles,
      placeholder: (provided: CSSObject) => ({
        ...provided,
        fontSize: '16px',
      }),
    }
  }

  if (small) {
    customStyles = {
      ...customStyles,
      control: (provided: CSSObject) => ({
        ...provided,
        height: '31',
        minHeight: '31',
      }),
      valueContainer: (provided: CSSObject) => ({
        ...provided,
        top: '-10%',
      }),
    }
  }

  if (noStyle) {
    customStyles = {
      ...customStyles,
      control: (provided: CSSObject) => {
        return {
          ...provided,
          border: artificalDarkMode
            ? '1px solid black'
            : '0px solid transparent',
          backgroundColor: artificalDarkMode ? 'white' : 'transparent',
          '&:hover': {},
          color,
        }
      },
      valueContainer: (provided: CSSObject) => {
        return {
          ...provided,
        }
      },
      placeholder: (provided: CSSObject) => ({ ...provided, color }),
      singleValue: (provided: CSSObject) => ({ ...provided, color }),
      multiValue: (provided: CSSObject) => ({
        ...provided,
        color,
        backgroundColor: 'transparent',
      }),
      multiValueLabel: (provided: CSSObject) => ({
        ...provided,
        color,
      }),
    }
  }

  if (isInvalid) {
    customStyles.control = (provided: CSSObject) => {
      return {
        ...provided,
        border: '1px solid rgb(241, 85, 108)',
      }
    }
  }

  return customStyles
}

export const getCustomElement = ({
  hideDropdowComponents = {},
  customDropdownIndicator,
  color,
  valuePrefix,
  slim = false,
  hasTooltip = false,
  propComponents,
  isMulti = false,
}: TGetCustomElement): any => {
  const customComponents = {
    Option: components.Option,
    ...(propComponents || {}),
    ...hideDropdowComponents,
    DropdownIndicator: customDropdownIndicator
      ? (p) => (
          <components.DropdownIndicator {...p}>
            <Icon icon="fas fa-chevron-down" color={color as SemanticCOLORS} />
          </components.DropdownIndicator>
        )
      : components.DropdownIndicator,
  }

  // always remove separator

  // eslint-disable-next-line react/display-name
  customComponents.IndicatorSeparator = (): React.ReactElement => <div />

  if (valuePrefix) {
    customComponents.ValueContainer = PrefixedValueContainer(valuePrefix) as any
  }

  if (slim) {
    customComponents.IndicatorsContainer = SlimIndicatorsContainer as any // fuck this dumpster component
  }

  if (hasTooltip) {
    // eslint-disable-next-line react/display-name
    customComponents.Option = (props): React.ReactElement => {
      const comp = <components.Option {...props} />
      //  required using React.Fragment to show tooltip

      return (
        <>
          <Tooltip
            label={get(props, 'data.tooltip', '')}
            style={{ display: 'block' }}
            withArrow
            withinPortal
            zIndex={2000}
          >
            <div>{comp}</div>
          </Tooltip>
        </>
      )

      // return (
      //   <TooltipTrigger
      //     tooltip={get(props, 'data.tooltip', '')}
      //     // isPopover
      //     positionFixed
      //     style={{ zIndex: 1001 }}
      //   >
      //     <>{comp}</>
      //   </TooltipTrigger>
      // )
    }
  }

  if (hasTooltip && isMulti) {
    // eslint-disable-next-line react/display-name
    customComponents.MultiValueContainer = (props): React.ReactElement => {
      const comp = <components.MultiValueContainer {...props} />

      return (
        <>
          <Tooltip
            label={get(props, 'data.tooltip', '')}
            style={{ display: 'block' }}
            withArrow
            withinPortal
          >
            <div>{comp}</div>
          </Tooltip>
        </>
      )
    }
  }

  // eslint-disable-next-line react/display-name
  customComponents.ClearIndicator = (cProps): React.ReactElement => {
    const {
      innerProps: { ref, ...restInnerProps },
    } = cProps

    return (
      <div {...restInnerProps} ref={ref}>
        <div style={{ marginTop: '4px' }}>
          <Icon
            icon="fas fa-times-circle"
            tooltip={i18n.t('clear_content', 'Inhalt leeren')}
            onClick={noop}
          />
        </div>
      </div>
    )
  }

  return customComponents
}
